import { Component } from '@theme/component';

class ProductStickyDetails extends Component {
  static moveToMain = true;

  /** @type {boolean} */
  #buyButtonsVisible = false;

  /** @type {boolean} */
  #footerVisible = false;

  get sectionId() {
    return this.dataset.sectionId;
  }

  get productId() {
    return this.dataset.productId;
  }

  /**
   * Main PDP buy buttons (the anchor we observe).
   * Important: exclude any product form rendered inside this sticky bar,
   * otherwise it creates a feedback loop (show -> target intersects -> hide -> show...).
   */
  get mainBuyButtons() {
    const selector = `product-form-component[data-section-id="${CSS.escape(this.sectionId)}"][data-product-id="${CSS.escape(this.productId)}"]`;

    // There may be multiple matches (main PDP + sticky bar). Always pick the one outside of this element.
    const candidates = Array.from(document.querySelectorAll(selector));
    const mainTarget = candidates.find((el) => !this.contains(el));

    return mainTarget || null;
  }

  /**
   * First element in the footer group to observe for visibility.
   */
  get footerGroup() {
    return document.querySelector('.shopify-section-group-footer-group');
  }

  /**
   * The <main> container we want this component to be a direct child of.
   */
  get main() {
    return document.getElementById('MainContent') || document.querySelector('main');
  }

  /** @type {number | undefined} */
  _connectedAt;

  /** @type {string | undefined} */
  _instanceId;

  connectedCallback() {
    super.connectedCallback();
    this._connectedAt = Date.now();
    this._instanceId = `${Date.now()}-${Math.random()}`;

    this.#moveToMain();
    this.#setupIntersectionObservers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#disconnectObservers();
  }

  #moveToMain() {
    if (!ProductStickyDetails.moveToMain) return;

    const main = this.main;
    if (!main) return;

    // Ensure the component becomes a direct child of <main>.
    if (this.parentElement === main) return;

    // Insert at the beginning of <main> to avoid ending up after content/sections.
    main.insertBefore(this, main.firstChild);
  }

  #setupIntersectionObservers() {
    this.#disconnectObservers();

    const buyTarget = this.mainBuyButtons;
    const footerTarget = this.footerGroup;

    this.buyButtonsObserver = this.#createVisibilityObserver(
      buyTarget,
      (visible) => {
        this.#buyButtonsVisible = visible;
      },
      '-100px 0px 0px 0px'
    );
    this.footerObserver = this.#createFooterObserver(footerTarget);

    if (buyTarget) this.#buyButtonsVisible = this.#isInViewport(buyTarget, 100);
    if (footerTarget) {
      this.#footerVisible =
        footerTarget.getBoundingClientRect().top < window.innerHeight;
    }
    this.#updateVisibility();
  }

  /**
   * @param {Element | null} target
   * @param {(visible: boolean) => void} setVisible
   * @param {string} [rootMargin]
   * @returns {IntersectionObserver | undefined}
   */
  #createVisibilityObserver(target, setVisible, rootMargin) {
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        setVisible(entry.isIntersecting);
        this.#updateVisibility();
      },
      { threshold: 0, rootMargin: rootMargin ?? '0px' }
    );
    observer.observe(target);
    return observer;
  }

  /**
   * Footer observer: hide sticky when footer is in view OR when we've scrolled past it
   * (footer top above viewport), so the bar stays hidden at the very bottom of the page.
   * @param {Element | null} target
   * @returns {IntersectionObserver | undefined}
   */
  #createFooterObserver(target) {
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const inFooterZone =
          entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight;
        this.#footerVisible = inFooterZone;
        this.#updateVisibility();
      },
      { threshold: 0 }
    );
    observer.observe(target);
    return observer;
  }

  /** @param {Element} el @param {number} marginTop */
  #isInViewport(el, marginTop) {
    const rect = el.getBoundingClientRect();
    return rect.bottom > marginTop && rect.top < window.innerHeight;
  }

  #disconnectObservers() {
    if (this.buyButtonsObserver) {
      this.buyButtonsObserver.disconnect();
      this.buyButtonsObserver = null;
    }
    if (this.footerObserver) {
      this.footerObserver.disconnect();
      this.footerObserver = null;
    }

    this.#buyButtonsVisible = false;
    this.#footerVisible = false;
  }

  /**
   * Updates visibility based on both buy buttons and footer visibility.
   * Show only if neither buy buttons nor footer are visible.
   */
  #updateVisibility() {
    const shouldHide = this.#buyButtonsVisible || this.#footerVisible;

    if (shouldHide) {
      this.#hide();
    } else {
      this.#show();
    }
  }

  /** @param {TransitionEvent} event */
  #onTransitionEnd = (event) => {
    if (event.target !== this || event.propertyName !== 'transform') return;
    this.dataset.animating = 'false';
  };

  #show() {
    if (this.dataset.visible === 'true') return;

    this.dataset.animating = 'true';
    this.dataset.visible = 'true';
    this.addEventListener('transitionend', this.#onTransitionEnd, { once: true });
  }

  #hide() {
    if (this.dataset.visible !== 'true') return;

    this.dataset.animating = 'true';
    this.dataset.visible = 'false';
    this.addEventListener('transitionend', this.#onTransitionEnd, { once: true });
  }

  reinitialize() {
    this.#disconnectObservers();

    this.#moveToMain();
    this.#setupIntersectionObservers();
  }
}

if (!customElements.get('product-sticky-details')) {
  customElements.define('product-sticky-details', ProductStickyDetails);
}

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  /** @param {CustomEvent<{sectionId: string}>} e */
  const handleSectionLoad = (e) => {
    const eventSectionId = e.detail?.sectionId;
    if (!eventSectionId || typeof eventSectionId !== 'string') return;

    // Use requestAnimationFrame to ensure sticky bars have finished moving to main
    requestAnimationFrame(() => {
      const allSticky = document.querySelectorAll('product-sticky-details');
      const matchingElements = Array.from(allSticky).filter(
        (el) => el instanceof ProductStickyDetails && el.sectionId === eventSectionId
      );

      if (matchingElements.length <= 1) return;

      const main = document.getElementById('MainContent') ?? document.querySelector('main');
      const inMain = matchingElements.filter((el) => el.parentElement === main);
      if (inMain.length <= 1) return;

      // Keep the newest based on timestamp and instance ID
      const newest = inMain.reduce((a, b) => {
        const aTime = a._connectedAt ?? 0;
        const bTime = b._connectedAt ?? 0;
        if (aTime === bTime) {
          // If timestamps are equal, use instance ID for stable sorting
          return (a._instanceId ?? '') > (b._instanceId ?? '') ? a : b;
        }
        return aTime >= bTime ? a : b;
      });

      for (const el of inMain) {
        if (el === newest) continue;
        el.remove();
      }
    });
  };

  /** @param {CustomEvent<{sectionId: string}>} e */
  const handleBlockEvent = (e) => {
    const eventSectionId = e.detail?.sectionId;
    if (!eventSectionId) return;

    for (const el of document.querySelectorAll('product-sticky-details')) {
      if (!(el instanceof ProductStickyDetails)) continue;
      if (el.sectionId !== eventSectionId) continue;
      el.reinitialize();
    }
  };

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:reorder', handleSectionLoad);
  document.addEventListener('shopify:block:select', handleBlockEvent);
  document.addEventListener('shopify:block:deselect', handleBlockEvent);
}
